import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  reportsApi,
  ReportsQuery,
  CategoryStatRow,
  FaultStatRow,
  RepeatOffenderRow,
  FaultPeopleReport,
  CategoryInsight,
  DetailCategoryRow,
  DetailTicketRow,
  TicketReport,
  WeeklySendResult
} from '@/api/reports.api';
import { useAuth } from '@/context/AuthContext';
import {
  CATEGORY_CODES,
  CATEGORY_LABEL,
  CategoryCode,
  FAULT_ATTRIBUTION_VALUES,
  FAULT_ATTRIBUTION_LABELS,
  FaultAttribution
} from '@/types/domain';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Badge } from '@/components/ui/Badge';

type ReportTab = 'detailed' | 'insights' | 'by-category' | 'by-fault' | 'repeat-offenders';

/**
 * V4.9.10 — Admin Reports page.
 *
 * Four tabs that surface different lenses on the same dataset:
 *   • By Handler — who's processing how many tickets, breach rate %
 *   • By Category — totals + avg resolution time per category
 *   • By Fault   — fault attribution breakdown (sales / shipping / etc.)
 *   • Repeat Offenders — people flagged most on fault-attributed tickets
 *
 * Date range picker offers presets (Last 7 days, 30 days, 90 days,
 * 1 year, this/last month, YTD) plus a custom from/to calendar. CSV
 * export downloads matching tickets for further analysis.
 *
 * Gated at the route by RequireCapability `reports.view` (V4.9.22: Admin +
 * Specialist "solvers" have it by default; tunable per-user in Permissions).
 * The component re-checks the capability defensively for mid-session changes.
 */
export function ReportsPage() {
  const { user, hasCapability, capabilitiesLoaded } = useAuth();
  const navigate = useNavigate();
  // V4.9.22 — Reports is now capability-gated (`reports.view`), not role-gated.
  // Admin has it (all caps) and Specialists — the ticket "solvers" — get it by
  // default; Idris can also grant/revoke it per-user via the Permissions matrix.
  const canViewReports = hasCapability('reports.view');
  const [tab, setTab] = useState<ReportTab>('detailed');
  const [datePreset, setDatePreset] = useState<string>('last_30');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [faults, setFaults] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  // V4.9.12 — AI Insights tab state. The AI call is explicit (Generate button)
  // because it costs quota + takes a few seconds; insightRunId bumps per run.
  const [insightCat, setInsightCat] = useState<string>('A1');
  const [insightRunId, setInsightRunId] = useState<number>(0);
  const [insightExporting, setInsightExporting] = useState<'' | 'md' | 'csv'>('');

  // Defensive in-page gate — the route guard (RequireCapability) already does
  // this. Only bounce once we're confident the capability set is populated
  // (server-confirmed OR a non-empty role-defaults seed), so nobody is kicked
  // out during the brief capabilities-loading window.
  useEffect(() => {
    // Only bounce once the AUTHORITATIVE capability set has loaded — a per-user
    // grant isn't in the role-default seed, so redirecting on the seed alone would
    // wrongly kick out someone an admin just granted reports.view to.
    if (user && capabilitiesLoaded && !canViewReports) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, capabilitiesLoaded, canViewReports, navigate]);

  // Compute the actual ReportsQuery from the controls.
  const reportsQuery = useMemo<ReportsQuery>(() => {
    const range = computeRange(datePreset, customFrom, customTo);
    return {
      from: range.from,
      to: range.to,
      category: categories.length > 0 ? categories : undefined,
      fault: faults.length > 0 ? faults : undefined
    };
  }, [datePreset, customFrom, customTo, categories, faults]);

  const categoryQuery = useQuery<CategoryStatRow[]>({
    queryKey: ['reports', 'by-category', reportsQuery],
    queryFn: () => reportsApi.byCategory(reportsQuery),
    enabled: tab === 'by-category' && canViewReports
  });
  const faultQuery = useQuery<FaultStatRow[]>({
    queryKey: ['reports', 'by-fault', reportsQuery],
    queryFn: () => reportsApi.byFault(reportsQuery),
    enabled: tab === 'by-fault' && canViewReports
  });
  const repeatQuery = useQuery<RepeatOffenderRow[]>({
    queryKey: ['reports', 'repeat-offenders', reportsQuery],
    queryFn: () => reportsApi.repeatOffenders(reportsQuery),
    enabled: tab === 'repeat-offenders' && canViewReports
  });
  // V4.9.19 — people manually marked at fault (shown alongside the AI fault breakdown).
  const faultPeopleQuery = useQuery<FaultPeopleReport>({
    queryKey: ['reports', 'fault-people', reportsQuery],
    queryFn: () => reportsApi.faultPeople(reportsQuery),
    enabled: tab === 'by-fault' && canViewReports
  });

  // V4.9.12 — AI insight runs only when the user clicks Generate (runId > 0).
  // staleTime Infinity + the backend's 30-min cache mean no surprise re-spend.
  const insightsQuery = useQuery<CategoryInsight>({
    queryKey: ['reports', 'insights', insightCat, reportsQuery.from, reportsQuery.to, insightRunId],
    queryFn: () =>
      reportsApi.insights({ category: insightCat, from: reportsQuery.from, to: reportsQuery.to }),
    enabled: tab === 'insights' && insightRunId > 0 && !!insightCat && canViewReports,
    staleTime: Infinity,
    retry: false
  });

  const onInsightExport = async (kind: 'md' | 'csv') => {
    setInsightExporting(kind);
    try {
      const q = { category: insightCat, from: reportsQuery.from, to: reportsQuery.to };
      const blob = kind === 'md' ? await reportsApi.exportInsightMd(q) : await reportsApi.exportInsightCsv(q);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bds-insight-${insightCat}-${new Date().toISOString().slice(0, 10)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInsightExporting('');
    }
  };

  const onExport = async () => {
    // Download through the shared axios client (see reports.api.ts) so
    // the request carries the Bearer token, honours VITE_API_URL, and
    // gets the transparent 401 refresh. Then turn the Blob into a file
    // download the browser saves.
    setExporting(true);
    try {
      const blob = await reportsApi.exportCsv(reportsQuery);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bds-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(false);
    }
  };

  if (!user || !canViewReports) {
    return (
      <div className="state">
        <h4>No access</h4>
        <p>You don&apos;t have access to Reports. Ask an admin to enable it for your account.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>Reports</h2>
        {tab !== 'insights' && tab !== 'detailed' ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void onExport()}
            disabled={exporting}
          >
            {exporting ? 'Preparing CSV…' : 'Download CSV'}
          </button>
        ) : null}
      </div>

      {/* Filter bar */}
      <div className="card card-overflow-visible">
        <div className="card-body">
          <div className="filter-bar">
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Date range</label>
              <select
                className="form-select"
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
              >
                <option value="last_7">Last 7 days</option>
                <option value="last_30">Last 30 days</option>
                <option value="last_90">Last 90 days</option>
                <option value="last_year">Last 365 days</option>
                <option value="this_month">This month</option>
                <option value="last_month">Last month</option>
                <option value="this_year">This year (YTD)</option>
                <option value="custom">Custom range&hellip;</option>
              </select>
            </div>
            {datePreset === 'custom' ? (
              <>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">From</label>
                  <input
                    type="date"
                    className="form-input"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">To</label>
                  <input
                    type="date"
                    className="form-input"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
              </>
            ) : null}
            {tab !== 'insights' && tab !== 'detailed' ? (
              <>
                <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={categories[0] ?? ''}
                    onChange={(e) => setCategories(e.target.value ? [e.target.value] : [])}
                  >
                    <option value="">All categories</option>
                    {CATEGORY_CODES.map((c) => (
                      <option key={c} value={c}>
                        {c} · {CATEGORY_LABEL[c as CategoryCode] ?? c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
                  <label className="form-label">Fault attribution</label>
                  <select
                    className="form-select"
                    value={faults[0] ?? ''}
                    onChange={(e) => setFaults(e.target.value ? [e.target.value] : [])}
                  >
                    <option value="">All faults (incl. unattributed)</option>
                    {FAULT_ATTRIBUTION_VALUES.map((f) => (
                      <option key={f} value={f}>
                        {FAULT_ATTRIBUTION_LABELS[f]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs mt-4">
        {(
          [
            { id: 'detailed' as ReportTab, label: '📋 Detailed' },
            { id: 'insights' as ReportTab, label: '✨ AI Insights' },
            { id: 'by-category' as ReportTab, label: 'By Category' },
            { id: 'by-fault' as ReportTab, label: 'Fault Tracking' },
            { id: 'repeat-offenders' as ReportTab, label: 'Repeat Offenders' }
          ]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`dashboard-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="dashboard-tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="dashboard-tab-body">
        {tab === 'detailed' ? (
          <DetailedPanel reportsQuery={reportsQuery} />
        ) : tab === 'insights' ? (
          <InsightsPanel
            query={insightsQuery}
            category={insightCat}
            onCategory={setInsightCat}
            onGenerate={() => setInsightRunId((n) => n + 1)}
            hasRun={insightRunId > 0}
            runId={insightRunId}
            exporting={insightExporting}
            onExport={(k) => void onInsightExport(k)}
          />
        ) : tab === 'by-category' ? (
          <CategoryTable query={categoryQuery} />
        ) : tab === 'by-fault' ? (
          <div className="flex flex-col gap-4">
            <FaultTable query={faultQuery} />
            <FaultPeopleTable query={faultPeopleQuery} />
          </div>
        ) : (
          <RepeatOffendersTable query={repeatQuery} />
        )}
      </div>
    </div>
  );
}

function CategoryTable({ query }: { query: UseQueryResult<CategoryStatRow[]> }) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const rows = query.data ?? [];
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Tickets by category</h3>
        <span className="text-muted text-small">
          Volume + average resolution time per category.
        </span>
      </div>
      <div className="card-body no-pad">
        {rows.length === 0 ? (
          <div className="state"><h4>No data</h4></div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total</th>
                  <th>Completed</th>
                  <th>Cancelled</th>
                  <th>Breached</th>
                  <th>Avg resolution (hrs)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.category} className="no-hover">
                    <td>
                      <div style={{ fontFamily: 'monospace' }}>{r.category}</div>
                      <div className="text-small text-muted">
                        {CATEGORY_LABEL[r.category as CategoryCode] ?? '—'}
                      </div>
                    </td>
                    <td>{r.total}</td>
                    <td>{r.completed}</td>
                    <td>{r.cancelled}</td>
                    <td>{r.breached}</td>
                    <td>{r.avg_resolution_hours !== null ? `${r.avg_resolution_hours}h` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * V4.9.19 — People manually marked at fault by a Solver/Admin (the Fault
 * panel), aggregated by person + team over finished tickets in range. Sits
 * beside the AI single-category fault breakdown.
 */
function FaultPeopleTable({ query }: { query: UseQueryResult<FaultPeopleReport> }) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const data = query.data;
  const people = data?.people ?? [];
  const teams = data?.teams ?? [];
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">People manually marked at fault</h3>
        <span className="text-muted text-small">
          Solver/Admin marks across finished tickets — {data?.total_marked_tickets ?? 0} ticket
          {(data?.total_marked_tickets ?? 0) === 1 ? '' : 's'}, {data?.total_marks ?? 0} mark
          {(data?.total_marks ?? 0) === 1 ? '' : 's'}.
        </span>
      </div>
      <div className="card-body no-pad">
        {people.length === 0 ? (
          <div className="state"><h4>No manual fault marks in this range</h4></div>
        ) : (
          <>
            {teams.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 16px 0' }}>
                {teams.map((t) => (
                  <span
                    key={t.team}
                    style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: '#fee2e2',
                      color: '#991b1b',
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    {t.team_label}: {t.tickets}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Team</th>
                    <th>Tickets at fault</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p, i) => (
                    <tr key={`${p.team}-${p.name}-${i}`} className="no-hover">
                      <td>{p.name}</td>
                      <td>{p.team_label}</td>
                      <td>{p.tickets}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FaultTable({ query }: { query: UseQueryResult<FaultStatRow[]> }) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const rows = query.data ?? [];
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Fault tracking</h3>
        <span className="text-muted text-small">
          Who's at fault — sales / shipping / vendor / customer / design / internal / none.
        </span>
      </div>
      <div className="card-body no-pad">
        {rows.length === 0 ? (
          <div className="state">
            <h4>No fault data yet</h4>
            <p className="text-small">
              Open any ticket &rarr; set the "Fault attribution" dropdown &rarr; it'll appear in this report.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fault</th>
                  <th>Tickets</th>
                  <th>% of total</th>
                  <th>Top categories</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.fault} className="no-hover">
                    <td>
                      <strong>
                        {FAULT_ATTRIBUTION_LABELS[r.fault as FaultAttribution] ?? r.fault}
                      </strong>
                    </td>
                    <td>{r.total}</td>
                    <td>
                      <Badge
                        tone={r.pct_of_total >= 30 ? 'danger' : r.pct_of_total >= 15 ? 'warning' : 'info'}
                      >
                        {r.pct_of_total}%
                      </Badge>
                    </td>
                    <td>
                      {(r.top_categories ?? []).map((c) => (
                        <Badge key={c.category} tone="neutral" className="ml-1">
                          {c.category} ({c.count})
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RepeatOffendersTable({ query }: { query: UseQueryResult<RepeatOffenderRow[]> }) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const rows = query.data ?? [];
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Repeat offenders</h3>
        <span className="text-muted text-small">
          People whose names appear most often on fault-attributed tickets.
        </span>
      </div>
      <div className="card-body no-pad">
        {rows.length === 0 ? (
          <div className="state">
            <h4>No repeat offenders yet</h4>
            <p className="text-small">
              Once admin tags fault attribution on tickets, this list shows the people whose
              names come up the most — useful for spotting patterns.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Fault tickets</th>
                  <th>Fault breakdown</th>
                  <th>Top categories</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.user_id}-${r.role}`} className="no-hover">
                    <td>
                      <div>{r.name}</div>
                      <div className="text-small text-muted">@{r.alias}</div>
                    </td>
                    <td>
                      <Badge tone="neutral">{r.role}</Badge>
                    </td>
                    <td>
                      <strong>{r.fault_tickets}</strong>
                    </td>
                    <td>
                      {(r.fault_breakdown ?? []).map((f) => (
                        <Badge key={f.fault} tone="warning" className="ml-1">
                          {FAULT_ATTRIBUTION_LABELS[f.fault as FaultAttribution] ?? f.fault}: {f.count}
                        </Badge>
                      ))}
                    </td>
                    <td>
                      {(r.top_categories ?? []).map((c) => (
                        <Badge key={c.category} tone="neutral" className="ml-1">
                          {c.category} ({c.count})
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== V4.9.12 — AI Insights ===================== */

function faultLabel(party: string): string {
  if (party === 'unclassified') return 'Unclassified (not grouped by AI)';
  return FAULT_ATTRIBUTION_LABELS[party as FaultAttribution] ?? party;
}

function HBar({
  label,
  value,
  count,
  max,
  tone,
  onClick
}: {
  label: string;
  value: number;
  count: number;
  max: number;
  tone?: 'fault';
  /** V4.9.18 — when set, the bar is clickable → drills into its exact tickets. */
  onClick?: () => void;
}) {
  const w = Math.max(4, Math.round((value / max) * 100));
  const clickable = !!onClick;
  return (
    <div
      className={clickable ? 'insight-bar-clickable' : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      title={clickable ? `View the ${count} ticket${count === 1 ? '' : 's'} behind “${label}”` : label}
      style={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr 96px',
        alignItems: 'center',
        gap: 10,
        padding: '6px 6px',
        borderRadius: 8,
        cursor: clickable ? 'pointer' : 'default'
      }}
    >
      <span className="text-small" style={{ fontWeight: 600 }} title={label}>
        {label}
      </span>
      <div style={{ height: 9, borderRadius: 99, background: 'var(--s3, #eef0f4)', overflow: 'hidden' }}>
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${w}%`,
            borderRadius: 99,
            background: tone === 'fault' ? 'linear-gradient(90deg,#e23b3b,#f59e0b)' : 'var(--primary, #4f46e5)'
          }}
        />
      </div>
      <span className="text-small text-muted" style={{ textAlign: 'right' }}>
        {count} · {Math.round(value)}%{clickable ? ' →' : ''}
      </span>
    </div>
  );
}

/**
 * V4.9.18 — clickable drill-down behind a Root cause / Whose-fault bar. Shows
 * the EXACT tickets the AI assigned to that group, with the real order #,
 * customer (the client), status, culprit and every handler (owner / solver /
 * communicator) by name. Each row opens the full ticket. This is the dedicated
 * "go to those tickets" interface admins asked for.
 */
function InsightDrilldown({
  insight,
  drill,
  onBack
}: {
  insight: CategoryInsight;
  drill: { title: string; subtitle: string; ids: string[] };
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const idSet = new Set(drill.ids);
  const rows = insight.tickets.filter((t) => idSet.has(t.id));
  return (
    <div className="card">
      <div
        className="card-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <h3 className="card-title">{drill.title}</h3>
          <span className="text-muted text-small">{drill.subtitle} · click any ticket to open it</span>
          <div className="text-small text-muted" style={{ marginTop: 2 }}>
            Grouped by the AI's inferred cause/fault. The “Stored fault” column is each ticket's saved
            attribution (may differ, or be unset).
          </div>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>
          ← Back to insight
        </button>
      </div>
      <div className="card-body no-pad">
        {rows.length === 0 ? (
          <div className="state">
            <h4>No traceable tickets for this group</h4>
            <p className="text-small">The AI didn't return ticket references for this bar on this run — Regenerate to retry.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Stored fault</th>
                  <th>Handled by</th>
                  <th>Root cause</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${t.id}`)}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{t.id}</span>
                    </td>
                    <td className="text-small">{t.order_number ?? '—'}</td>
                    <td className="text-small">{t.customer_name ?? '—'}</td>
                    <td className="text-small">{t.status}</td>
                    <td>
                      <Badge tone={t.fault_attribution ? 'warning' : 'neutral'}>{t.culprit_label}</Badge>
                    </td>
                    <td className="text-small">
                      {t.owner ? <div><span className="text-muted">Owner:</span> {t.owner}</div> : null}
                      {t.solver ? <div><span className="text-muted">Solver:</span> {t.solver}</div> : null}
                      {t.communicator ? <div><span className="text-muted">Comm:</span> {t.communicator}</div> : null}
                      {!t.owner && !t.solver && !t.communicator ? '—' : null}
                    </td>
                    <td className="text-small" style={{ maxWidth: 280 }}>{t.root_cause_preview}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function InsightReport({ r }: { r: CategoryInsight }) {
  const maxCause = Math.max(1, ...r.root_causes.map((c) => c.share_pct));
  const maxFault = Math.max(1, ...r.fault_breakdown.map((f) => f.share_pct));
  // V4.9.16 — the people behind the fault. Computed from the DB (deterministic,
  // accurate names), scoped to this category + range, finished tickets only.
  const peopleQuery = useQuery<RepeatOffenderRow[]>({
    queryKey: ['reports', 'insight-people', r.category, r.range.from, r.range.to],
    queryFn: () =>
      reportsApi.repeatOffenders({ from: r.range.from, to: r.range.to, category: [r.category] })
  });
  const people = peopleQuery.data ?? [];
  // V4.9.18 — drill state: which Root cause / Whose-fault group is expanded into
  // its exact tickets. null = show the normal insight grids.
  const [drill, setDrill] = useState<{ title: string; subtitle: string; ids: string[] } | null>(null);
  const pct = (n: number) => Math.round(n);
  const sub = (count: number, share: number) => `${count} ticket${count === 1 ? '' : 's'} · ${pct(share)}%`;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {r.category} · {r.category_label} — why these happened
          </h3>
          <span className="text-muted text-small">
            {r.tickets_analyzed} finished ticket{r.tickets_analyzed === 1 ? '' : 's'} analysed · {r.model}
            {r.truncated ? ' · latest 60' : ''}
          </span>
        </div>
        <div className="card-body">
          <p style={{ lineHeight: 1.65, margin: 0 }}>{r.executive_summary}</p>
          <p className="text-small text-muted" style={{ margin: '8px 0 0' }}>
            Tip: click any <strong>Root cause</strong> or <strong>Whose fault</strong> bar to see the exact tickets behind it.
          </p>
        </div>
      </div>

      {drill ? (
        <InsightDrilldown insight={r} drill={drill} onBack={() => setDrill(null)} />
      ) : (
      <>
      <div className="insight-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Root causes</h3>
            <span className="text-muted text-small">Why the tickets happened — click to drill in</span>
          </div>
          <div className="card-body">
            {r.root_causes.length === 0 ? (
              <span className="text-small text-muted">No causes identified.</span>
            ) : (
              r.root_causes.map((c, i) => (
                <HBar
                  key={i}
                  label={c.cause}
                  value={c.share_pct}
                  count={c.count}
                  max={maxCause}
                  onClick={
                    c.ticket_ids.length
                      ? () => setDrill({ title: `Root cause — ${c.cause}`, subtitle: sub(c.count, c.share_pct), ids: c.ticket_ids })
                      : undefined
                  }
                />
              ))
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Whose fault</h3>
            <span className="text-muted text-small">Accountability split — click to drill in</span>
          </div>
          <div className="card-body">
            {r.fault_breakdown.length === 0 ? (
              <span className="text-small text-muted">No fault attributed.</span>
            ) : (
              r.fault_breakdown.map((f, i) => (
                <HBar
                  key={i}
                  label={faultLabel(f.party)}
                  value={f.share_pct}
                  count={f.count}
                  max={maxFault}
                  tone="fault"
                  onClick={
                    f.ticket_ids.length
                      ? () => setDrill({ title: `Whose fault — ${faultLabel(f.party)}`, subtitle: sub(f.count, f.share_pct), ids: f.ticket_ids })
                      : undefined
                  }
                />
              ))
            )}
            {peopleQuery.isLoading ? (
              <div className="text-small text-muted" style={{ marginTop: 12 }}>Loading who handled these…</div>
            ) : peopleQuery.isError ? (
              <div className="text-small text-muted" style={{ marginTop: 12 }}>
                Couldn't load the handler names — see the <strong>Detailed</strong> tab for the per-person breakdown.
              </div>
            ) : people.length > 0 ? (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--s3, #eef0f4)', paddingTop: 10 }}>
                <div
                  className="text-small text-muted"
                  style={{ marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}
                >
                  By person — who handled these (per role held)
                </div>
                {people.slice(0, 8).map((p) => (
                  <div
                    key={`${p.user_id}-${p.role}`}
                    className="text-small"
                    style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', gap: 8 }}
                  >
                    <span>
                      {p.name} <span className="text-muted">· {p.role}</span>
                    </span>
                    <strong>{p.fault_tickets}</strong>
                  </div>
                ))}
                <div className="text-small text-muted" style={{ marginTop: 6 }}>
                  Full per-ticket breakdown is in the <strong>Detailed</strong> tab.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="insight-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Patterns</h3>
          </div>
          <div className="card-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              {r.key_patterns.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recommendations</h3>
            <span className="text-muted text-small">What to fix</span>
          </div>
          <div className="card-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              {r.recommendations.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function InsightsPanel({
  query,
  category,
  onCategory,
  onGenerate,
  hasRun,
  runId,
  exporting,
  onExport
}: {
  query: UseQueryResult<CategoryInsight>;
  category: string;
  onCategory: (c: string) => void;
  onGenerate: () => void;
  hasRun: boolean;
  /** Bumped on every Generate/Regenerate — used to remount InsightReport so the
   *  drill-down state resets even when the backend serves a cached payload. */
  runId: number;
  exporting: '' | 'md' | 'csv';
  onExport: (kind: 'md' | 'csv') => void;
}) {
  const r = query.data;
  const showReport = hasRun && !query.isFetching && !query.isError && r && !r.empty;
  return (
    <div>
      <div className="card card-overflow-visible">
        <div className="card-body">
          <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 260 }}>
              <label className="form-label">Category to analyse</label>
              <select className="form-select" value={category} onChange={(e) => onCategory(e.target.value)}>
                {CATEGORY_CODES.map((c) => (
                  <option key={c} value={c}>
                    {c} · {CATEGORY_LABEL[c as CategoryCode] ?? c}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="btn btn-primary" onClick={onGenerate} disabled={query.isFetching}>
              {query.isFetching ? 'Analysing…' : hasRun ? 'Regenerate' : 'Generate insight'}
            </button>
            {showReport ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!!exporting}
                  onClick={() => onExport('md')}
                >
                  {exporting === 'md' ? 'Exporting…' : 'Export report (.md)'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!!exporting}
                  onClick={() => onExport('csv')}
                >
                  {exporting === 'csv' ? 'Exporting…' : 'Export source (.csv)'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-4">
        {!hasRun ? (
          <div className="card">
            <div className="card-body">
              <div className="state">
                <h4>AI category insights</h4>
                <p className="text-small">
                  Pick a category and click <strong>Generate insight</strong>. The AI reads the resolution notes of
                  finished tickets and explains <strong>why</strong> they happened, <strong>whose</strong> fault it was,
                  and <strong>what to fix</strong> — crisp and to the point.
                </p>
              </div>
            </div>
          </div>
        ) : query.isFetching ? (
          <LoadingState label="Reading resolution notes & generating the report…" large />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : r && r.empty ? (
          <div className="card">
            <div className="card-body">
              <div className="state">
                <h4>Nothing to analyse yet</h4>
                <p className="text-small">{r.executive_summary}</p>
              </div>
            </div>
          </div>
        ) : r ? (
          // key on runId (bumped each Generate/Regenerate) + category so a fresh
          // run ALWAYS remounts InsightReport with clean drill state — even when
          // the backend serves a cached payload (same generated_at).
          <InsightReport key={`${r.category}-${runId}`} r={r} />
        ) : null}
      </div>
    </div>
  );
}

/* ===================== V4.9.14 — Detailed per-ticket report ===================== */

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * V4.9.14 — Detailed report tab. Three-level drill-down:
 *   per-category count cards → click → ticket list → click → that ticket's
 *   OWN report (root cause + who the culprit is). Plus a detailed CSV export
 *   and the weekly-email card.
 */
function DetailedPanel({ reportsQuery }: { reportsQuery: ReportsQuery }) {
  const [cat, setCat] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Reset the drill-down whenever the date range changes.
  const rangeKey = `${reportsQuery.from ?? ''}|${reportsQuery.to ?? ''}`;
  useEffect(() => {
    setCat(null);
    setTicketId(null);
  }, [rangeKey]);

  const catsQuery = useQuery<DetailCategoryRow[]>({
    queryKey: ['reports', 'detailed', 'categories', reportsQuery],
    queryFn: () => reportsApi.detailedCategories(reportsQuery)
  });
  const ticketsQuery = useQuery<DetailTicketRow[]>({
    queryKey: ['reports', 'detailed', 'tickets', cat, reportsQuery.from, reportsQuery.to],
    queryFn: () => reportsApi.detailedTickets(cat as string, reportsQuery),
    enabled: !!cat
  });
  // V4.9.16 — "whose fault, by person" for the drilled category. Reuses the
  // repeat-offenders aggregation scoped to this one category (finished tickets,
  // all handlers owner/solver/communicator).
  const peopleQuery = useQuery<RepeatOffenderRow[]>({
    queryKey: ['reports', 'detailed', 'fault-by-person', cat, reportsQuery.from, reportsQuery.to],
    queryFn: () =>
      reportsApi.repeatOffenders({ from: reportsQuery.from, to: reportsQuery.to, category: [cat as string] }),
    enabled: !!cat
  });
  const reportQuery = useQuery<TicketReport>({
    queryKey: ['reports', 'detailed', 'ticket', ticketId],
    queryFn: () => reportsApi.ticketReport(ticketId as string),
    enabled: !!ticketId
  });

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await reportsApi.exportDetailedCsv(reportsQuery);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bds-detailed-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AutoAttributeCard />
      <WeeklyEmailCard />

      <div className="section-header" style={{ marginTop: 4 }}>
        <div className="text-small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => {
              setCat(null);
              setTicketId(null);
            }}
            style={{ background: 'none', border: 'none', color: cat ? 'var(--primary)' : 'inherit', cursor: 'pointer', padding: 0, fontWeight: cat ? 600 : 700 }}
          >
            All categories
          </button>
          {cat ? (
            <>
              <span className="text-muted">/</span>
              <button
                type="button"
                onClick={() => setTicketId(null)}
                style={{ background: 'none', border: 'none', color: ticketId ? 'var(--primary)' : 'inherit', cursor: 'pointer', padding: 0, fontWeight: ticketId ? 600 : 700 }}
              >
                {cat}
              </button>
            </>
          ) : null}
          {ticketId ? (
            <>
              <span className="text-muted">/</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{ticketId}</span>
            </>
          ) : null}
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void onExport()} disabled={exporting}>
          {exporting ? 'Preparing…' : 'Download detailed report (.csv)'}
        </button>
      </div>

      {ticketId ? (
        <TicketReportCard query={reportQuery} />
      ) : cat ? (
        <CategoryDrilldown cat={cat} query={ticketsQuery} peopleQuery={peopleQuery} onSelect={setTicketId} />
      ) : (
        <CategoryCards query={catsQuery} onSelect={setCat} />
      )}
    </div>
  );
}

function CategoryCards({ query, onSelect }: { query: UseQueryResult<DetailCategoryRow[]>; onSelect: (c: string) => void }) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const rows = query.data ?? [];
  if (!rows.length) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="state">
            <h4>No tickets in this range</h4>
            <p className="text-small">Widen the date range above to see per-category counts.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-small text-muted" style={{ marginBottom: 10 }}>
        Counts <strong>finished</strong> tickets only (done + cancelled). Ongoing tickets are shown per card but{' '}
        <strong>not counted</strong> in the report. Click a card to see every ticket — its culprit and who handled it.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
        {rows.map((c) => (
          <button
            key={c.category}
            type="button"
            onClick={() => onSelect(c.category)}
            className="card"
            style={{ textAlign: 'left', cursor: 'pointer', border: '1px solid var(--border, #e5e7eb)' }}
          >
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.category}</span>
                <span
                  style={{ fontSize: 26, fontWeight: 800, color: 'var(--primary)' }}
                  title="Finished tickets (done + cancelled)"
                >
                  {c.total}
                </span>
              </div>
              <div className="text-small text-muted" style={{ marginBottom: 8 }}>{c.category_label}</div>
              <div className="text-small" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <span>✅ {c.completed} done</span>
                <span>🚫 {c.cancelled} cancelled</span>
              </div>
              {c.open > 0 || c.breached > 0 ? (
                <div
                  className="text-small text-muted"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}
                >
                  {c.open > 0 ? <span>🟠 {c.open} ongoing · not counted</span> : null}
                  {c.breached > 0 ? (
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>⚠ {c.breached} breached</span>
                  ) : null}
                </div>
              ) : null}
              <div className="text-small text-muted" style={{ marginTop: 6 }}>{c.fault_attributed} with culprit set →</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FaultByPersonCard({ people, loading }: { people: RepeatOffenderRow[]; loading: boolean }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Whose fault — by person</h3>
        <span className="text-muted text-small">
          People who handled the fault-attributed (finished) tickets in this category — one row per role a
          person held (owner / solver / communicator).
        </span>
      </div>
      <div className="card-body no-pad">
        {loading ? (
          <div className="card-body"><span className="text-small text-muted">Loading…</span></div>
        ) : people.length === 0 ? (
          <div className="state">
            <h4>No culprits attributed yet</h4>
            <p className="text-small">
              Set the fault on finished tickets (or let the AI do it) and the responsible people appear here.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Fault tickets</th>
                  <th>Fault breakdown</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={`${p.user_id}-${p.role}`} className="no-hover">
                    <td>
                      <div>{p.name}</div>
                      <div className="text-small text-muted">@{p.alias}</div>
                    </td>
                    <td><Badge tone="neutral">{p.role}</Badge></td>
                    <td><strong>{p.fault_tickets}</strong></td>
                    <td>
                      {(p.fault_breakdown ?? []).map((f) => (
                        <Badge key={f.fault} tone="warning" className="ml-1">
                          {FAULT_ATTRIBUTION_LABELS[f.fault as FaultAttribution] ?? f.fault}: {f.count}
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryDrilldown({
  cat,
  query,
  peopleQuery,
  onSelect
}: {
  cat: string;
  query: UseQueryResult<DetailTicketRow[]>;
  peopleQuery: UseQueryResult<RepeatOffenderRow[]>;
  onSelect: (id: string) => void;
}) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const rows = query.data ?? [];
  const people = peopleQuery.data ?? [];
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <FaultByPersonCard people={people} loading={peopleQuery.isLoading} />
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {cat} — {rows.length} finished ticket{rows.length === 1 ? '' : 's'}
          </h3>
          <span className="text-muted text-small">
            Finished tickets only (done + cancelled). Click a ticket for its full root cause + culprit.
          </span>
        </div>
        <div className="card-body no-pad">
          {rows.length === 0 ? (
            <div className="state">
              <h4>No finished tickets in this range</h4>
              <p className="text-small">Ongoing tickets aren't counted in the report.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th>Culprit (whose fault)</th>
                    <th>Handled by</th>
                    <th>Root cause</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => onSelect(t.id)}>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{t.id}</span>
                        <div className="text-small text-muted">
                          {t.store_code}
                          {t.order_number ? ` · ${t.order_number}` : ''}
                        </div>
                      </td>
                      <td>{t.status}</td>
                      <td>
                        <Badge tone={t.fault_attribution ? 'warning' : 'neutral'}>{t.culprit_label}</Badge>
                        {t.fault_attribution_source ? (
                          <div className="text-small text-muted" style={{ marginTop: 2 }}>
                            {t.fault_attribution_source === 'ai' ? '🧠 AI' : '✍️ manual'}
                          </div>
                        ) : null}
                      </td>
                      <td className="text-small">
                        {t.owner ? <div><span className="text-muted">Owner:</span> {t.owner}</div> : null}
                        {t.solver ? <div><span className="text-muted">Solver:</span> {t.solver}</div> : null}
                        {t.communicator ? <div><span className="text-muted">Comm:</span> {t.communicator}</div> : null}
                        {!t.owner && !t.solver && !t.communicator ? '—' : null}
                      </td>
                      <td className="text-small" style={{ maxWidth: 300 }}>{t.root_cause_preview}</td>
                      <td className="text-small text-muted">{fmtDate(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-muted">{label}</div>
      <div style={{ fontWeight: 600 }}>{value || '—'}</div>
    </div>
  );
}

function TicketReportCard({ query }: { query: UseQueryResult<TicketReport> }) {
  const navigate = useNavigate();
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const r = query.data;
  if (!r) return null;
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          <span style={{ fontFamily: 'monospace' }}>{r.id}</span> · {r.category} {r.category_label}
        </h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/tickets/${r.id}`)}>
          Open full ticket →
        </button>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 14px' }}>
            <div className="text-small text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Who is the culprit</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: r.culprit_set ? '#c2410c' : '#6b7280' }}>{r.culprit_label}</div>
            {!r.culprit_set ? (
              <div className="text-small text-muted" style={{ marginTop: 4 }}>Set “Fault attribution” on the ticket to record the culprit.</div>
            ) : null}
          </div>
          <div style={{ background: '#f8f9fb', border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px 14px' }}>
            <div className="text-small text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Status</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {r.status}
              {r.breached ? <span style={{ color: '#dc2626' }}> · ⚠ breached</span> : null}
            </div>
            <div className="text-small text-muted" style={{ marginTop: 4 }}>
              {r.severity} · {r.store_code}
              {r.order_number ? ` · ${r.order_number}` : ''}
            </div>
          </div>
        </div>

        <div>
          <div className="text-small text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Root cause</div>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{r.root_cause}</div>
        </div>

        <div className="text-small" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <ReportField label="Owner" value={r.owner} />
          <ReportField label="Solver" value={r.solver} />
          <ReportField label="Communicator" value={r.communicator} />
          <ReportField label="Raised by" value={r.raised_by} />
          <ReportField label="Customer" value={r.customer_name} />
          <ReportField label="Quotation type" value={r.quotation_type} />
          <ReportField label="Tracking #" value={r.tracking_number} />
          <ReportField label="Created" value={fmtDate(r.created_at)} />
          <ReportField label="Resolved" value={fmtDate(r.resolved_at)} />
        </div>

        {r.notes.length ? (
          <div>
            <div className="text-small text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Notes timeline</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
              {r.notes.map((n, i) => (
                <li key={i} className="text-small">{n}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AutoAttributeCard() {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{
    attributed: number;
    remaining: number;
    aiAvailable: boolean;
    note: string;
  } | null>(null);
  const countQuery = useQuery({
    queryKey: ['reports', 'unattributed-count'],
    queryFn: () => reportsApi.unattributedCount()
  });
  const run = useMutation({
    mutationFn: () => reportsApi.autoAttributeFaults(),
    onSuccess: (r) => {
      setResult(r);
      // Refresh the un-attributed count + the category cards (fault counts).
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    }
  });
  const count = countQuery.data?.count;
  return (
    <div className="card" style={{ borderLeft: '4px solid #7c3aed' }}>
      <div
        className="card-body"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <div style={{ fontWeight: 700 }}>🧠 AI culprit attribution</div>
          <div className="text-small text-muted">
            The AI fills each ticket's culprit (fault) <strong>automatically</strong> — it runs every 2 hours and
            on demand here. It only fills tickets with no culprit yet, so your <strong>manual</strong> calls are
            never overwritten.{' '}
            {typeof count === 'number' ? (
              <strong>
                {count} ticket{count === 1 ? '' : 's'} not yet attributed.
              </strong>
            ) : null}
          </div>
          {result ? (
            <div
              className="text-small"
              style={{ marginTop: 6, color: result.aiAvailable ? '#16a34a' : '#d97706' }}
            >
              {result.aiAvailable ? `✅ ${result.note} ${result.remaining} remaining.` : `ℹ︎ ${result.note}`}
            </div>
          ) : null}
          {run.isError ? (
            <div className="form-error text-small" style={{ marginTop: 6 }}>
              {run.error instanceof Error ? run.error.message : 'Run failed'}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => run.mutate()}
          disabled={run.isPending || count === 0}
        >
          {run.isPending ? 'Attributing…' : 'Run AI now'}
        </button>
      </div>
    </div>
  );
}

function WeeklyEmailCard() {
  const [result, setResult] = useState<WeeklySendResult | null>(null);
  const send = useMutation({
    mutationFn: () => reportsApi.sendWeekly(),
    onSuccess: (r) => setResult(r)
  });
  return (
    <div className="card" style={{ borderLeft: '4px solid #1e3a8a' }}>
      <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700 }}>📧 Weekly report email</div>
          <div className="text-small text-muted">
            Automatically emailed every <strong>Monday 10:00 AM IST</strong> to <strong>idris@carotechs.com</strong> — per-category
            counts, fault breakdown, top culprits &amp; notable tickets with their root cause.
          </div>
          {result ? (
            <div className="text-small" style={{ marginTop: 6, color: result.delivered === 'smtp' ? '#16a34a' : '#d97706' }}>
              {result.delivered === 'smtp' ? '✅ Sent' : 'ℹ︎ Generated (SMTP disabled on this server — logged only)'} to {result.recipient}
              {' · '}
              {result.totals.created} created (intake) / {result.totals.fault_attributed} finished with culprit set
            </div>
          ) : null}
          {send.isError ? (
            <div className="form-error text-small" style={{ marginTop: 6 }}>
              {send.error instanceof Error ? send.error.message : 'Send failed'}
            </div>
          ) : null}
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => send.mutate()} disabled={send.isPending}>
          {send.isPending ? 'Sending…' : 'Send now'}
        </button>
      </div>
    </div>
  );
}

/**
 * V4.9.10 — Resolve the date preset / custom range to ISO from / to
 * strings. Adapted to the Reports page presets (more options than the
 * ticket filter bar's 7-day shortcuts).
 */
function computeRange(preset: string, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date();
  const isoStart = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0).toISOString();
  const isoEnd = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();

  switch (preset) {
    case 'last_7': {
      const d = new Date(now); d.setDate(now.getDate() - 7);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'last_30': {
      const d = new Date(now); d.setDate(now.getDate() - 30);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'last_90': {
      const d = new Date(now); d.setDate(now.getDate() - 90);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'last_year': {
      const d = new Date(now); d.setDate(now.getDate() - 365);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'this_month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'last_month': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: isoStart(s), to: isoEnd(e) };
    }
    case 'this_year': {
      const d = new Date(now.getFullYear(), 0, 1);
      return { from: isoStart(d), to: isoEnd(now) };
    }
    case 'custom': {
      const out: { from?: string; to?: string } = {};
      if (customFrom) out.from = `${customFrom}T00:00:00.000Z`;
      if (customTo) out.to = `${customTo}T23:59:59.999Z`;
      return out;
    }
    default:
      return {};
  }
}
